import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  ChevronRight,
  History,
  Monitor,
  Search,
} from "lucide-react";
import type { EnrichedCreditPackage } from "@/hooks/use-credit-packages-with-devices";
import { format } from "date-fns";
import DeviceCreditHistory from "@/components/devices/DeviceCreditHistory";

interface CreditManagementDevicesTabProps {
  packages: EnrichedCreditPackage[];
}

interface DeviceRow {
  device_id: string;
  device_name: string | null;
  device_serial: string | null;
  totalCredits: number;
  remainingCredits: number;
  usedCredits: number;
  packages: EnrichedCreditPackage[];
  hasReserved: boolean;
}

export const CreditManagementDevicesTab = ({ packages }: CreditManagementDevicesTabProps) => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [historyDeviceId, setHistoryDeviceId] = useState<string | null>(null);

  const rows: DeviceRow[] = useMemo(() => {
    const byDevice: Record<string, DeviceRow> = {};
    for (const pkg of packages) {
      if (!pkg.device_id || pkg.device_id === "USER_OWNED") continue;
      if (!byDevice[pkg.device_id]) {
        byDevice[pkg.device_id] = {
          device_id: pkg.device_id,
          device_name: pkg.device_name ?? null,
          device_serial: pkg.device_serial ?? null,
          totalCredits: 0,
          remainingCredits: 0,
          usedCredits: 0,
          packages: [],
          hasReserved: false,
        };
      }
      const row = byDevice[pkg.device_id];
      row.totalCredits += pkg.total_credits;
      row.remainingCredits += pkg.remaining_credits;
      row.usedCredits += pkg.total_credits - pkg.remaining_credits;
      row.packages.push(pkg);
      if (pkg.is_released === false) row.hasReserved = true;
    }
    return Object.values(byDevice).sort((a, b) => b.usedCredits - a.usedCredits);
  }, [packages]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.device_id.toLowerCase().includes(q) ||
        (r.device_name ?? "").toLowerCase().includes(q) ||
        (r.device_serial ?? "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-10"
            placeholder="Search by device name, serial or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Badge variant="outline">{filteredRows.length} devices</Badge>
      </div>

      {filteredRows.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No devices with credit packages match the current search.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredRows.map((row) => {
            const isOpen = !!expanded[row.device_id];
            const pct =
              row.totalCredits > 0
                ? Math.round((row.usedCredits / row.totalCredits) * 100)
                : 0;
            const status =
              row.remainingCredits === 0
                ? { label: "Depleted", cls: "bg-gray-100 text-gray-700" }
                : row.hasReserved
                ? { label: "Active + Reserved", cls: "bg-teal-100 text-teal-800" }
                : { label: "Active", cls: "bg-green-100 text-green-800" };

            return (
              <Card key={row.device_id}>
                <Collapsible
                  open={isOpen}
                  onOpenChange={(open) =>
                    setExpanded((prev) => ({ ...prev, [row.device_id]: open }))
                  }
                >
                  <CardHeader className="py-3">
                    <div className="flex items-center gap-3">
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <Monitor className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">
                          {row.device_name || row.device_serial || row.device_id}
                        </CardTitle>
                        <div className="text-xs text-muted-foreground truncate">
                          {row.device_serial ? `SN ${row.device_serial} · ` : ""}
                          ID {row.device_id.slice(0, 8)}…
                        </div>
                      </div>
                      <div className="hidden md:flex items-center gap-6 text-sm">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Used</div>
                          <div className="font-semibold">
                            {row.usedCredits.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Remaining</div>
                          <div className="font-semibold">
                            {row.remainingCredits.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Total</div>
                          <div className="font-semibold">
                            {row.totalCredits.toLocaleString()}
                          </div>
                        </div>
                        <div className="w-32">
                          <Progress value={pct} />
                          <div className="text-xs text-muted-foreground mt-1 text-right">
                            {pct}% used
                          </div>
                        </div>
                      </div>
                      <Badge className={status.cls}>{status.label}</Badge>
                      <Badge variant="outline">{row.packages.length} pkg</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryDeviceId(row.device_id)}
                      >
                        <History className="h-4 w-4 mr-1" />
                        History
                      </Button>
                    </div>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="border-t pt-3 space-y-2">
                        {row.packages.map((pkg) => (
                          <div
                            key={pkg.id}
                            className="flex items-center justify-between gap-3 p-2 rounded border bg-muted/30"
                          >
                            <div className="min-w-0">
                              <div className="font-medium text-sm truncate">
                                {pkg.package_name}
                                {pkg.year_number ? ` · Year ${pkg.year_number}` : ""}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {pkg.assignment_type.replace("_", " ")} ·{" "}
                                Created {format(new Date(pkg.created_at), "MMM dd, yyyy")}
                                {pkg.release_date
                                  ? ` · Releases ${format(new Date(pkg.release_date), "MMM dd, yyyy")}`
                                  : ""}
                              </div>
                            </div>
                            <div className="text-right text-sm">
                              <div className="font-medium">
                                {pkg.remaining_credits.toLocaleString()} /{" "}
                                {pkg.total_credits.toLocaleString()}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {pkg.is_released === false ? "Reserved" : pkg.is_assigned ? "Assigned" : "Available"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={!!historyDeviceId}
        onOpenChange={(open) => !open && setHistoryDeviceId(null)}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Device Credit History</DialogTitle>
          </DialogHeader>
          {historyDeviceId && (
            <DeviceCreditHistory deviceId={historyDeviceId} autoLoad />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditManagementDevicesTab;
